<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <v-toolbar flat color="white">
            <v-toolbar-title>Users</v-toolbar-title>
            <v-spacer/>
            <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    clearable
                    @click:clear="handleClearSearch"
                    v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <v-icon large class="mr-2" @click="loadItems">
                mdi-refresh
            </v-icon>
            <v-dialog v-model="dialogLocal" max-width="500px">
                <v-card>
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">{{ formTitle }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-progress-linear v-if="waitingOnServer" :indeterminate="true"/>
                        <v-container>
                            <v-row dense>
                                <v-col md="12">
                                    <v-text-field
                                            v-model="userData.displayName"
                                            label="Display Name"
                                            :clearable="!readonly"
                                            single-line
                                            :readonly="readonly">
                                    </v-text-field>
                                </v-col>
                                <v-col md="12">
                                    <v-row dense>
                                        <v-text-field
                                                v-model="userData.emailAddress"
                                                label="Email Address"
                                                :clearable="!readonly"
                                                single-line
                                                :readonly="readonly">
                                        </v-text-field>
                                        <v-checkbox class="ml-5"
                                            type="checkbox"
                                            label="Update?"
                                            v-model="updateEmailAddress"
                                            :disabled="readonly"
                                            color="primary"/>
                                    </v-row>
                                </v-col>
                                <v-col md="12">
                                     <v-select
                                             v-model="userData.role"
                                             :items="userRoles"
                                             label="User Role"
                                             single-line
                                             :readonly="readonly">
                                    </v-select>
                                </v-col>
                                <v-col md="12">
                                    <v-select
                                             v-model="userData.maxAppLevel"
                                             :items="maxAppLevels"
                                             label="Max App Level"
                                             single-line
                                             :readonly="readonly">
                                    </v-select>
                                </v-col>
                                <v-col xs="12">
                                    <v-checkbox
                                            type="checkbox"
                                            label="Enable Subscription Override"
                                            v-model="override"
                                            :disabled="readonly"
                                            color="primary"/>
                                    <v-menu
                                            v-if="override"
                                            ref="overrideEndDateMenu"
                                            v-model="overrideEndDateMenu"
                                            :disabled="readonly"
                                            :close-on-content-click="false"
                                            :nudge-right="40"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                    v-model="overrideEndDateFormatted"
                                                    :disabled="readonly"
                                                    label="Subscription Override End Date"
                                                    prepend-icon="mdi-calendar"
                                                    v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker color="blue" v-model="overrideEndDate" no-title @input="overrideEndDateMenu = false"/>
                                    </v-menu>
                                </v-col>
                                <v-col md="12">
                                    <v-select
                                        v-model="userData.testProtoSuite"
                                        :items="testProtocolSuiteNames"
                                        label="Testing Protocol Suite"
                                        single-line
                                        :readonly="readonly">
                                    </v-select>
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total PLR Tests Taken:</strong> {{ totalPlrTestsForUser }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total NPC Tests Taken:</strong> {{ totalNpcTestsForUser }}
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions class="mr-2">
                        <v-spacer/>
                        <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
                        <v-btn color="blue darken-1" text :disabled="readonly" @click="save">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        :headers="getHeaders()"
                        :items="users"
                        item-key="id"
                        @current-items="currentItems"
                        :options.sync="pagination"
                        :server-items-length="totalRecords"
                        :loading="waiting"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.viewing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="viewItem(item)">
                                                mdi-compass
                                            </v-icon>
                                        </span>
                                </template>
                                <span>View</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.editing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="editItem(item)">
                                                mdi-pencil
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Edit</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.deleting>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="deleteItem(item)">
                                                mdi-delete
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmationDialog v-if="confirmDeleteUser" :params="confirmDeleteUserParams" @confirmation-dialog-response="confirmDeleteUserHandler"></ConfirmationDialog>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import { userRoles } from '@/store/users'
    import { maxAppLevels } from '@/store/users'
    import ConfirmationDialog from '@/components/confirmation-dialog'

    export default
    {
        name: "users",
        components:
        {
            ConfirmationDialog
        },
        data: function ()
        {
            return {
                tipDisabling:
                {
                    viewing: false,
                    editing: false,
                    deleting: false
                },
                dialogLocal: false,
                waitingOnServer: false,
                pagination: {},
                search: '',
                readonly: false,
                editedIndex: -1,
                userData: {overrideSubscription: null},
                updateEmailAddress: false,
                userRoles: userRoles,
                maxAppLevels: maxAppLevels,
                override: false,
                overrideEndDateMenu: false,
                overrideEndDate: null,
                overrideEndDateFormatted: null,
                confirmDeleteUser: false,
                confirmDeleteUserParams:
                {
                    title: 'Delete User',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                }
            }
        },
        destroyed()
        {
            this.CLEAR_USERS()
        },
        computed:
        {
            ...mapState('users',
            {
                users: state => state.users,
                totalRecords: state => state.totalRecords,
                totalPlrTestsForUser: state => state.totalPlrTestsForUser,
                totalNpcTestsForUser: state => state.totalNpcTestsForUser,
                testProtocolSuiteNames: state => state.testProtocolSuiteNames
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            formTitle()
            {
                if (this.readonly)
                {
                    return "View User"
                }
                return 'Edit User'
            }
        },
        watch:
        {
            search()
            {
                this.pagination.page = 1
            },
            dialogLocal(val)
            {
                // If the dialog is being opened
                if (val)
                {
                    if (this.userData.overrideSubscription)
                    {
                        this.overrideEndDateFormatted = this.formatDate(this.userData.overrideSubscription);
                        const dt = new Date(this.userData.overrideSubscription)
                        this.overrideEndDate = `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}-${dt.getDate().toString().padStart(2, '0')}`
                    }
                }
            },
            overrideEndDate(val)
            {
                this.overrideEndDateFormatted = this.formatDate(val)
            },
        },
        methods:
        {
            ...mapActions('users', ['GET_USERS', 'UPDATE_USER', 'DELETE_USER', 'GET_TEST_COUNT_FOR_USER', 'GET_TEST_PROTOCOL_SUITE_NAMES']),
            ...mapMutations('users', ['CLEAR_USERS', 'SET_PLR_TEST_COUNT_FOR_USER', 'SET_NPC_TEST_COUNT_FOR_USER']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            async handleSearch()
            {
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.search = '';
                await this.loadItems()
            },
            async loadItems()
            {
                if (this.orgId === '')
                {
                    return
                }
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_USERS({pagination: this.pagination, search: this.search})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            getHeaders()
            {
                return [
                    {
                        text: 'Actions',
                        width: '150px',
                        value: 'actions',
                        sortable: false
                    },
                    {
                        text: 'Display Name',
                        width: '200px',
                        value: 'displayName',
                        sortable: false
                    },
                    {
                        text: 'Email Address',
                        width: '200px',
                        value: 'emailAddress',
                        sortable: false
                    },
                    {
                        text: 'User ID',
                        value: 'uId',
                        sortable: false
                    },
                    {
                        text: 'Last Login',
                        width: '200px',
                        value: 'accessedDate',
                        sortable: false
                    },
                    {
                        text: 'Download Date',
                        width: '200px',
                        value: 'createdAt',
                        sortable: false
                    },
                    {
                        text: 'Trial Expires Date',
                        width: '200px',
                        value: 'freeTrialEndDate',
                        sortable: false
                    },
                    {
                        text: 'Subscription Expires Date',
                        width: '200px',
                        value: 'subscription.expiresDate',
                        sortable: false
                    },
                    {
                        text: 'Subscription Type',
                        width: '300px',
                        value: 'subscription.subId',
                        sortable: false
                    },
                    {
                        text: 'Role',
                        value: 'authorities[0].role',
                        sortable: false
                    },
                    {
                        text: 'Version',
                        value: 'appVersion',
                        sortable: false
                    },
                    {
                        text: 'Pro Version',
                        width: '100px',
                        value: 'appProVersion',
                        sortable: false
                    },
                    {
                        text: 'Device',
                        width: '200px',
                        value: 'deviceName',
                        sortable: false
                    },
                    {
                        text: 'Pro Device',
                        width: '200px',
                        value: 'deviceProName',
                        sortable: false
                    },
                    {
                        text: 'OS',
                        value: 'osName',
                        sortable: false
                    },
                    {
                        text: 'Pro OS',
                        width: '100px',
                        value: 'osProName',
                        sortable: false
                    },
                    {
                        text: 'Browser Info',
                        width: '400px',
                        value: 'browserInfo',
                        sortable: false
                    }
                ]
            },
            currentItems(items)
            {
                if (this.pagination.page !== 1 && 0 === items.length)
                {
                    this.pagination.page -= 1
                }
            },
            async viewItem(item)
            {
                this.tipDisabling.viewing= true;
                this.readonly = true;
                this.updateEmailAddress = false;
                try
                {
                    this.HIDE_SNACKBAR();
                    this.waitingOnServer = true;
                    await this.GET_TEST_COUNT_FOR_USER({uId: item.uId})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.waitingOnServer = false;
                await this.commonViewEditItem(item)
            },
            async editItem(item)
            {
                this.tipDisabling.editing= true;
                this.readonly = false;
                this.SET_PLR_TEST_COUNT_FOR_USER(0);
                this.SET_NPC_TEST_COUNT_FOR_USER(0);
                await this.commonViewEditItem(item)
            },
            async commonViewEditItem(item)
            {
                // Only request the protocol suites once per session
                if (0 === this.testProtocolSuiteNames.length)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.waitingOnServer = true;
                        await this.GET_TEST_PROTOCOL_SUITE_NAMES()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                    this.waitingOnServer = false;
                }
                this.editedIndex = this.users.indexOf(item);
                this.userData.uId = item.uId;
                this.userData.displayName = item.displayName;
                this.userData.emailAddress = item.emailAddress;
                this.overrideEndDate = this.formatDate(item.overrideSubscription);
                this.override = this.overrideEndDate != null;
                this.userData.role = item.authorities[0].role;
                this.userData.maxAppLevel = item.maxAppLevel;
                this.userData.testProtoSuite = item.testProtoSuite;
                this.dialogLocal = true
            },
            async deleteItem(item)
            {
                this.tipDisabling.deleting= true;
                this.confirmDeleteUserParams.text = `Are you sure you want to delete user: ${item.displayName}?`;
                this.confirmDeleteUserParams.context = item;
                this.confirmDeleteUser = true
            },
            async confirmDeleteUserHandler(payload)
            {
                this.confirmDeleteUser = false;
                // If the user should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        await this.DELETE_USER({deleteIndex: this.users.indexOf(payload), uId: payload.context.uId});
                        this.loadItems()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            close()
            {
                this.waitingOnServer = false;
                this.dialogLocal = false;
                setTimeout(() =>
                {
                    this.readonly = false;
                }, 300)
            },
            async save()
            {
                this.HIDE_SNACKBAR();
                this.waitingOnServer = true;
                this.readonly = true;
                try
                {
                    // If the user should not have the override subscription option turned on
                    if (!this.override)
                    {
                        this.userData.overrideSubscription = null
                    }
                    // If the user has None set for their testing protocol suite, change back to null
                    if ('None' === this.userData.testProtoSuite)
                    {
                        this.userData.testProtoSuite = null
                    }
                    if (!this.updateEmailAddress)
                    {
                        this.userData.emailAddress = undefined
                    }
                    await this.UPDATE_USER({editedIndex: this.editedIndex, data: this.userData})
                    this.updateEmailAddress = false;
                }
                catch (error)
                {
                    this.waitingOnServer = false;
                    this.readonly = false;
                    this.SHOW_SNACKBAR_ERROR(error.message);
                    return
                }
                this.close();
                this.loadItems()
            },
            formatDate(date)
            {
                if (!date)
                {
                    return null
                }
                // Account for date with . as separators
                if (date.split('T')[0].includes('.'))
                {
                    return date
                }
                const theDate = new Date(date);
                this.userData.overrideSubscription = theDate.toISOString();
                return theDate.toLocaleDateString(navigator.languages[0], {timeZone: 'UTC'})
            }
        }
    }
</script>

<style scoped>
</style>